import deepEquals from 'fast-deep-equal'

import { Mergeable, MergeableAs, MergeableList, MergeableObject, MergeData, PartialMergeResult, scalarConflict, SyncState } from '../types'

import { mergeArrays } from './mergeArrays'
import { mergeObjects } from './mergeObjects'
import { mergeConflictingStrings } from './mergeStrings'

export default function merge<T extends Mergeable<unknown>>(data: MergeData<T>): PartialMergeResult<T> {
  if (nonNullData(data)) {
    if (isMergeableAsArray(data)) {
      return mergeArrays(asCompleteMergeData<MergeableList<T>>(data, [] as unknown as MergeableList<T>), merge) as unknown as PartialMergeResult<T>
    }
    if (isMergeableAsObjects(data)) {
      return mergeObjects(asCompleteMergeData<MergeableObject<unknown>>(data, {} as unknown as MergeableObject<T>), merge) as unknown as PartialMergeResult<T>
    }
  }

  const { local, server } = data
  const state = getMergeState(data)
  const inSync = state === 'IN_SYNC'
  const modifications = inSync ? data.server : local

  const result = {
    state,
    modifications,
    nonConflictingModifications: modifications,
    conflicts: [],
  }
  if (state !== 'CONFLICT') {
    return result
  }

  if (isMergeableAsStrings(data)) {
    return mergeConflictingStrings(asCompleteMergeData<string>(data, '')) as unknown as PartialMergeResult<T>
  }

  return {
    state: 'CONFLICT',
    modifications: local,
    nonConflictingModifications: server,
    conflicts: [
      scalarConflict(data),
    ],
  }
}

function nonNullData(data: MergeData<Mergeable<unknown>>): boolean {
  return /* data.original != null && */ data.server != null && data.local != null
}

function isMergeableAsArray<T>(data: MergeData<Mergeable<T>>): data is MergeableAs<MergeableList<T>> {
  return isMergeableAs(Array.isArray, data)
}

function isMergeableAsObjects<T>(data: MergeData<Mergeable<T>>): data is MergeableAs<MergeableObject<T>> {
  return isMergeableAs(value => typeof value === 'object' && !Array.isArray(value), data)
}

function isMergeableAsStrings(data: MergeData<Mergeable<unknown>>): data is MergeableAs<string> {
  return isMergeableAs(value => typeof value === 'string', data)
}

function isMergeableAs(predicate: (v: unknown) => boolean, data: MergeData<Mergeable<unknown>>): boolean {
  return (predicate(data.original) || isNullish(data.original))
    && predicate(data.server)
    && predicate(data.local)
}

function isNullish(value: unknown): boolean {
  return value === null || value === undefined
}

function asCompleteMergeData<T>(partialData: MergeableAs<T>, defaultValue: T): MergeData<T> {
  if (partialData.original !== null && partialData.original !== undefined) {
    return partialData as MergeData<T>
  }

  return {
    ...partialData,
    original: partialData.original ?? defaultValue,
  }
}

function getMergeState<T extends Mergeable<unknown>>(
  { server, original, local }: MergeData<T>,
): SyncState {
  const modifiedLocally = !deepEquals(original, local)
  if (!modifiedLocally) {
    return 'IN_SYNC'
  }

  const modifiedOnServer = !deepEquals(original, server)
  const conflict = !deepEquals(server, local)

  if (!modifiedOnServer) { // & modified locally
    return 'MODIFIED_LOCALLY'
  }
  if (!conflict) { // Both have same modifications
    return 'IN_SYNC'
  }

  return 'CONFLICT'
}
